import { Fragment, useEffect, useRef, useState } from 'react';
import { Navigate, NavLink, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  IconButton,
  InputAdornment,
  LinearProgress,
  List,
  ListItemButton,
  ListSubheader,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  useMediaQuery,
  Typography,
} from '@mui/material';
import DashboardOutlined from '@mui/icons-material/DashboardOutlined';
import Inventory2Outlined from '@mui/icons-material/Inventory2Outlined';
import InsightsOutlined from '@mui/icons-material/InsightsOutlined';
import ShowChartOutlined from '@mui/icons-material/ShowChartOutlined';
import ReceiptLongOutlined from '@mui/icons-material/ReceiptLongOutlined';
import ShoppingCartOutlined from '@mui/icons-material/ShoppingCartOutlined';
import WidgetsOutlined from '@mui/icons-material/WidgetsOutlined';
import HandshakeOutlined from '@mui/icons-material/HandshakeOutlined';
import RequestQuoteOutlined from '@mui/icons-material/RequestQuoteOutlined';
import GroupsOutlined from '@mui/icons-material/GroupsOutlined';
import Refresh from '@mui/icons-material/Refresh';
import Logout from '@mui/icons-material/Logout';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutlineOutlined';
import LocalShippingOutlined from '@mui/icons-material/LocalShippingOutlined';
import {
  can,
  nextStep,
  orderFeed,
  roleLabel,
  workbenchOf,
  type OrderFeedRow,
  type Permission,
  type SessionUser,
} from '@zilian/contracts';
import { PAY_METHODS } from './PayDialog';
import { api, ApiError, configureApi } from './api';
import TradeOrderDialog from './TradeOrderDialog';
import LineQuantityDialog, { type QuantityLine } from './LineQuantityDialog';
import { Empty, decimalInput, money, skuPicker, type Field } from './ui';
import { Warehouse } from './pages/Warehouse';
import { Login } from './pages/Login';
import { Activate } from './pages/Activate';
import { Dashboard } from './pages/Dashboard';
import { Orders } from './pages/Orders';
import { Inventory } from './pages/Inventory';
import { Products } from './pages/Products';
import { Materials } from './pages/Materials';
import { BrandLogo } from '@zilian/ui';
import { Wordmark } from '@zilian/ui';
import { Partners } from './pages/Partners';
import { TradeStats } from './pages/TradeStats';
import { LocalOrderDetail } from './pages/LocalOrderDetail';
import { PickingBoard } from './pages/PickingBoard';
import { Settings } from './pages/Settings';
import { OrderDetail } from './pages/OrderDetail';
import { Statements } from './pages/Statements';
import { StatementDetail } from './pages/StatementDetail';
import { LocalStatementDetail } from './pages/LocalStatementDetail';
/**
 * Daily work first, then the records it draws on, then management. 工作台 stands alone. Each
 * entry names the permission that opens it (contracts/permissions.ts); a member only sees, and
 * can only route to, what their roles open. 工作台 is for office roles; warehouse staff start at
 * 仓库作业 instead.
 */
type NavItem = readonly [
  path: string,
  label: string,
  icon: typeof DashboardOutlined,
  permission: Permission | 'office',
];
const navGroups: { label: string; items: NavItem[] }[] = [
  { label: '', items: [['/', '工作台', DashboardOutlined, 'office']] },
  {
    label: '业务',
    items: [
      ['/purchases', '采购管理', ShoppingCartOutlined, 'purchase'],
      ['/sales', '销售管理', ReceiptLongOutlined, 'sales'],
    ],
  },
  {
    label: '仓储',
    items: [
      ['/warehouse', '仓库作业', LocalShippingOutlined, 'warehouse'],
      ['/inventory', '库存', Inventory2Outlined, 'inventory'],
    ],
  },
  {
    label: '资料',
    items: [
      ['/products', '商品档案', WidgetsOutlined, 'products'],
      ['/partners', '往来单位', HandshakeOutlined, 'partners'],
      ['/materials', '原料行情', ShowChartOutlined, 'materials'],
    ],
  },
  {
    label: '综合',
    items: [
      ['/statements', '对账结算', RequestQuoteOutlined, 'statements'],
      ['/stats', '交易统计', InsightsOutlined, 'stats'],
      ['/settings', '企业设置', GroupsOutlined, 'settings'],
    ],
  },
];
interface DialogState {
  title: string;
  description?: string;
  fields: Field[];
  form: Record<string, any>;
  key: string;
  /** A hint under the fields for what is filled so far, with an optional way out of the form. */
  notice?: (
    form: Record<string, any>,
  ) => { text: string; action?: { label: string; run: () => void } } | null;
  submit: (form: any, key: string) => Promise<unknown>;
  /** The confirm button's words when the action has a name of its own, e.g. 确认重置. */
  confirmLabel?: string;
}
export default function App() {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [workspace, setWorkspace] = useState('');
  const [initial, setInitial] = useState(true);
  const [bootstrap, setBootstrap] = useState<any>(null);
  const [orders, setOrders] = useState<any[]>([]);
  const [localOrders, setLocalOrders] = useState<any[]>([]);
  const [version, setVersion] = useState(0);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const [dialogError, setDialogError] = useState('');
  const [busy, setBusy] = useState(false);
  const [orderEntry, setOrderEntry] = useState<'BUY' | 'SELL' | null>(null);
  const [postingOrder, setPostingOrder] = useState<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const current = user?.workspaces.find((w) => w.id === workspace);
  const roles = current?.roles ?? [];
  // 'office': the trade workbench, for everyone but warehouse-only staff.
  const opens = (permission: Permission | 'office') =>
    permission === 'office' ? workbenchOf(roles) === 'office' : can(roles, permission);
  const home = workbenchOf(roles) === 'warehouse' ? '/warehouse' : '/';
  const groups = navGroups
    .map((g) => ({ ...g, items: g.items.filter((item) => opens(item[3])) }))
    .filter((g) => g.items.length);
  const nav = groups.flatMap((g) => g.items);
  configureApi(user, workspace);
  const reload = () => setVersion((v) => v + 1);
  const notify = (message: string) => setToast(message);
  const open = (data: Omit<DialogState, 'key'>) => {
    setDialogError('');
    setDialog({ ...data, key: crypto.randomUUID() });
  };
  const safe = async (run: () => Promise<unknown>) => {
    try {
      await run();
      reload();
    } catch (e) {
      setError((e as Error).message);
    }
  };
  useEffect(() => {
    api<SessionUser>('/auth/me')
      .then((next) => {
        setUser(next);
        setWorkspace(
          localStorage.getItem('zilian.workspace') &&
            next.workspaces.some((w) => w.id === localStorage.getItem('zilian.workspace'))
            ? localStorage.getItem('zilian.workspace')!
            : (next.workspaces[0]?.id ?? ''),
        );
      })
      .catch(() => undefined)
      .finally(() => setInitial(false));
  }, []);
  // Data belongs to one account and enterprise; switching either starts from empty.
  const loadedScope = useRef('');
  useEffect(() => {
    if (!user || !workspace) return;
    let active = true;
    setLoading(true);
    setError('');
    // A plain refresh (after a save) keeps the current data until the new data arrives, so the
    // page stays mounted and keeps its tab, search and page.
    const scope = `${user.id}|${workspace}`;
    if (loadedScope.current !== scope) {
      loadedScope.current = scope;
      setBootstrap(null);
      setOrders([]);
      setLocalOrders([]);
    }
    Promise.all([api('/bootstrap'), api<any[]>('/orders'), api<any[]>('/local-orders')])
      .then(([base, list, localList]) => {
        if (active) {
          setBootstrap(base);
          setOrders(list);
          setLocalOrders(localList);
        }
      })
      .catch((e) => {
        if (active) setError(e.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [user, workspace, version]);
  const submitDialog = async () => {
    if (!dialog || busy) return;
    setBusy(true);
    setDialogError('');
    try {
      await dialog.submit(dialog.form, dialog.key);
      setDialog(null);
      reload();
      notify('操作已完成');
    } catch (e) {
      // The order moved on (e.g. the partner just confirmed): retrying cannot help, so show
      // the latest state with the reason rather than leaving a dead dialog.
      if ((e as ApiError).stale) {
        setDialog(null);
        reload();
        notify((e as Error).message);
      } else setDialogError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const selectWorkspace = (id: string) => {
    setWorkspace(id);
    localStorage.setItem('zilian.workspace', id);
    navigate('/');
  };
  const warehouses = (bootstrap?.warehouses ?? []).map((w: any) => ({
    value: w.id,
    label: w.name,
  }));
  const skuOptions = (bootstrap?.skus ?? []).map((s: any) => ({
    value: s.id,
    label: `${s.code} · ${s.name}`,
  }));
  const newOrder = (side: 'SELL' | 'BUY' = 'SELL') => setOrderEntry(side);
  const feed = orderFeed(orders, localOrders, workspace);
  // A detail page keeps its list (采购 or 销售) highlighted, for shared and self-recorded orders.
  const detailSource = location.pathname.startsWith('/orders/')
    ? 'SHARED'
    : location.pathname.startsWith('/local-orders/')
      ? 'LOCAL'
      : null;
  const detailSide = detailSource
    ? feed.find(
        (order) => order.source === detailSource && order.id === location.pathname.split('/')[2],
      )?.side
    : undefined;
  // Matches styles.css: short screens show the data note's headline only.
  const shortScreen = useMediaQuery('(max-height: 860px)');
  const sectionActive = (path: string) =>
    path === '/'
      ? location.pathname === '/'
      : location.pathname.startsWith(path) ||
        (path === '/purchases' && detailSide === 'BUY') ||
        (path === '/sales' && detailSide === 'SELL') ||
        // Opened by someone who only moves this side's goods: it belongs to 仓库作业.
        (path === '/warehouse' &&
          !!detailSide &&
          !can(roles, detailSide === 'BUY' ? 'purchase' : 'sales'));
  const postLocal = (orderId: string) => {
    const order = localOrders.find((item) => item.id === orderId);
    if (!order || Number(order.remaining) <= 0) return;
    api<any>(`/local-orders/${orderId}`)
      .then(setPostingOrder)
      .catch((e) => setError(e.message));
  };
  /**
   * A list's next step done in place when its label says so (登记 / 确认 something), with the
   * same form the order's page uses; anything else (去…) opens the order. Returns whether it
   * was handled here.
   */
  const quickStep = (order: OrderFeedRow) => {
    const action = nextStep(order).action;
    if (order.source === 'LOCAL' && (action === 'COLLECT' || action === 'PAY')) {
      const selling = action === 'COLLECT';
      const left = Number(order.settlement_amount) - Number(order.paid ?? 0);
      open({
        title: selling ? '登记收款' : '登记付款',
        fields: [
          { name: 'amount', label: '金额（元）', decimals: 2 },
          {
            name: 'method',
            label: selling ? '收款方式' : '付款方式',
            options: Object.entries(PAY_METHODS).map(([value, label]) => ({ value, label })),
          },
          { name: 'reference', label: '凭据号 / 说明', wide: true },
        ],
        form: { amount: left.toFixed(2), method: 'BANK', reference: '' },
        submit: (f, key) => api(`/local-orders/${order.id}/payments`, { ...f, key }),
        description: `${order.peer} · ${order.number}，尚未结清 ${money(left)}。`,
      });
      return true;
    }
    if (order.source === 'SHARED' && action === 'COLLECT') {
      // The declared payment to check against the account; 未收到 is handled on the order.
      api<any>(`/orders/${order.id}`)
        .then((detail) => {
          const claim = (detail.claims ?? []).find((c: any) => c.status === 'CLAIMED');
          if (!claim) return reload();
          open({
            title: '确认收款',
            fields: [],
            form: {},
            confirmLabel: '确认收款',
            submit: () =>
              api(`/orders/${order.id}/payment-claims/${claim.id}`, { action: 'CONFIRM' }),
            description: `${order.peer} 声明已付 ${money(claim.amount)}（${PAY_METHODS[claim.method]}${claim.reference ? `，${claim.reference}` : ''}）。请先核对本企业账户已到账；未到账请打开订单点“未收到”。`,
          });
        })
        .catch((e) => setError(e.message));
      return true;
    }
    return false;
  };
  if (initial)
    return (
      <Box
        sx={{
          p: 8,
          textAlign: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  const signIn = (next: SessionUser) => {
    setUser(next);
    setWorkspace(next.workspaces[0]?.id ?? '');
  };
  const signOut = () =>
    safe(async () => {
      await api('/auth/logout', {});
      setUser(null);
      setBootstrap(null);
    });
  // An activation link works whether or not someone is signed in on this browser.
  if (location.pathname.startsWith('/activate/'))
    return (
      <Activate
        token={location.pathname.slice('/activate/'.length)}
        onLogin={(next) => {
          signIn(next);
          navigate('/', { replace: true });
        }}
      />
    );
  if (!user)
    return (
      <Login
        onLogin={(next) => {
          setUser(next);
          setWorkspace(next.workspaces[0]?.id ?? '');
        }}
      />
    );
  // The picking board fills a factory TV on its own: no sidebar or top bar.
  if (location.pathname === '/board') return <PickingBoard workspaceName={current?.name ?? ''} />;
  const common = {
    workspace,
    current,
    bootstrap,
    orders,
    version,
    reload,
    open,
    safe,
    notify,
    setError,
    warehouses,
    skuOptions,
    newOrder,
    feed,
    postLocal,
    quickStep,
    // Self-recorded orders open their own page, apart from two-party orders at /orders/:id.
    openLocal: (id: string) => navigate(`/local-orders/${id}`),
  };
  return (
    <>
      <aside className="app-sidebar">
        <div className="brand">
          <BrandLogo size={36} />
          <div className="brand-copy">
            <div className="brand-name">
              <Wordmark height={22} />
            </div>
            <div className="brand-sub">企业协同进销存</div>
          </div>
        </div>
        {/* MUI list: hover, focus and the selected state come from the components, so the
            menu behaves like every other MUI control. The class names only drive the narrow
            (icon-only) layout in styles.css. */}
        <List
          component="nav"
          aria-label="主菜单"
          // The enterprise is named in the top bar; group labels are small grey text only.
          // Scrolls on its own when a short screen can't fit every entry; the brand stays put.
          className="nav-list"
          sx={{ px: 1.75, pt: 0.5, pb: 1, flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}
        >
          {groups.map((group) => (
            <Fragment key={group.label || 'home'}>
              {group.label && (
                <ListSubheader
                  disableSticky
                  className="nav-label"
                  sx={{
                    px: 2,
                    mt: 1.25,
                    lineHeight: '24px',
                    fontSize: 12,
                    fontWeight: 500,
                    color: 'text.disabled',
                    bgcolor: 'transparent',
                  }}
                >
                  {group.label}
                </ListSubheader>
              )}
              {group.items.map(([path, label, Icon]) => {
                const active = sectionActive(path);
                return (
                  <ListItemButton
                    key={path}
                    component={NavLink}
                    to={path}
                    end={path === '/'}
                    selected={active}
                    className="nav-item"
                    sx={{
                      my: 0.25,
                      minHeight: 40,
                      py: 0.5,
                      // Same corners as the buttons, not a pill.
                      borderRadius: 1,
                      color: 'text.secondary',
                      '&.Mui-selected': { color: 'primary.main', fontWeight: 650 },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 34, color: 'inherit' }}>
                      <Icon sx={{ fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText
                      className="nav-text"
                      primary={label}
                      slotProps={{ primary: { sx: { fontSize: 14, fontWeight: 'inherit' } } }}
                    />
                  </ListItemButton>
                );
              })}
            </Fragment>
          ))}
        </List>
        {/* Always on: reassurance that this enterprise's data stays its own. Short screens keep
            the headline only, with the detail on hover, so the menu keeps its room. */}
        <Tooltip
          title={shortScreen ? '仅与合作方共享已授权的交易信息与交付进度。' : ''}
          placement="right"
        >
          <div className="sidebar-bottom">
            <Paper
              className="sidebar-note"
              sx={{ p: 2, bgcolor: '#f5f9f7', borderColor: '#e2ece6' }}
            >
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <CheckCircleOutline sx={{ fontSize: 16, color: 'primary.main' }} />
                <Typography sx={{ fontSize: 12 }}>企业数据独立管理</Typography>
              </Stack>
              <Typography
                className="sidebar-note-detail"
                color="text.secondary"
                sx={{ fontSize: 11, mt: 1, lineHeight: 1.7 }}
              >
                仅与合作方共享已授权的交易信息与交付进度。
              </Typography>
            </Paper>
          </div>
        </Tooltip>
      </aside>
      <main className="app-main">
        <header className="topbar">
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                bgcolor: bootstrap ? '#4b9d78' : '#d9a64c',
              }}
            />
            <Typography
              color="text.secondary"
              sx={{
                fontSize: 13,
              }}
            >
              企业协同进销存
            </Typography>
            <Typography color="divider">/</Typography>
            <Typography
              sx={{
                fontSize: 13,
              }}
            >
              {nav.find((n) => sectionActive(n[0]))?.[1] ?? '订单详情'}
            </Typography>
          </Stack>
          <Stack
            direction="row"
            spacing={2}
            sx={{
              alignItems: 'center',
            }}
          >
            {/* Only people who work for several enterprises (e.g. an accountant) get a switcher. */}
            {user.workspaces.length > 1 ? (
              <TextField
                select
                fullWidth={false}
                size="small"
                value={workspace}
                onChange={(e) => selectWorkspace(e.target.value)}
                sx={{ width: 200, flexShrink: 0 }}
                aria-label="当前企业"
              >
                {user.workspaces.map((w) => (
                  <MenuItem key={w.id} value={w.id}>
                    {w.name}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <Typography sx={{ fontSize: 14, fontWeight: 650 }} noWrap>
                {current?.name}
              </Typography>
            )}
            <Tooltip title="刷新数据">
              <IconButton onClick={reload}>
                <Refresh fontSize="small" />
              </IconButton>
            </Tooltip>
            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: 'center',
              }}
            >
              <Avatar
                sx={{ width: 32, height: 32, bgcolor: '#e8eeed', color: '#17665f', fontSize: 13 }}
              >
                {user.name.slice(0, 1)}
              </Avatar>
              <Box
                sx={{
                  display: { xs: 'none', md: 'block' },
                  minWidth: 90,
                  whiteSpace: 'nowrap',
                }}
              >
                <Typography
                  sx={{
                    fontSize: 12,
                  }}
                >
                  {user.name}
                </Typography>
                <Typography
                  color="text.secondary"
                  sx={{
                    fontSize: 10,
                  }}
                >
                  {roleLabel(roles)}
                </Typography>
              </Box>
            </Stack>
            <Tooltip title="退出登录">
              <IconButton onClick={signOut}>
                <Logout fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </header>
        {loading && <LinearProgress sx={{ height: 2 }} />}
        <div className="content">
          <ServiceNotice workspace={current} />
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          {bootstrap && (
            <Routes>
              {/* A page the member's roles do not open (an old link, a typed address) goes to
                  their own start page; the API refuses the data as well. */}
              {[
                ['/', 'office', <Dashboard {...common} />],
                ['/purchases', 'purchase', <Orders key="BUY" {...common} side="BUY" />],
                ['/sales', 'sales', <Orders key="SELL" {...common} side="SELL" />],
                ['/warehouse', 'warehouse', <Warehouse {...common} />],
                ['/orders/:id', 'office', <OrderDetail {...common} />],
                ['/local-orders/:id', 'office', <LocalOrderDetail {...common} />],
                ['/statements', 'statements', <Statements {...common} />],
                ['/statements/local/:id', 'statements', <LocalStatementDetail {...common} />],
                ['/statements/:id', 'statements', <StatementDetail {...common} />],
                ['/inventory', 'inventory', <Inventory {...common} />],
                ['/products', 'products', <Products {...common} />],
                ['/materials', 'materials', <Materials {...common} />],
                ['/partners', 'partners', <Partners {...common} />],
                ['/stats', 'stats', <TradeStats {...common} />],
                ['/settings', 'settings', <Settings {...common} />],
              ].map(([path, permission, page]) => (
                <Route
                  key={path as string}
                  path={path as string}
                  element={
                    opens(permission as Permission | 'office') ? (
                      page
                    ) : (
                      <Navigate to={home} replace />
                    )
                  }
                />
              ))}
              <Route path="/orders" element={<Navigate to="/purchases" replace />} />
              <Route path="/local-orders" element={<Navigate to="/purchases" replace />} />
              <Route path="*" element={<Navigate to={home} replace />} />
            </Routes>
          )}
          {!bootstrap && !loading && !error && <Empty>请选择可以访问的企业工作空间。</Empty>}
        </div>
      </main>
      {postingOrder && (
        <LineQuantityDialog
          key={postingOrder.id}
          title={postingOrder.kind === 'PURCHASE' ? '登记采购实收入库' : '登记销售出库'}
          subtitle={`${postingOrder.number} · ${postingOrder.counterparty} · 按实际数量登记，可分批完成`}
          verb={postingOrder.kind === 'PURCHASE' ? '入库' : '出库'}
          lines={postingOrder.lines.map((l: any): QuantityLine => ({
            id: l.id,
            name: l.sku_snapshot.name,
            spec: l.sku_snapshot.spec,
            // A sale's accessories leave with it.
            accessories: postingOrder.kind === 'SALE' ? l.accessories : undefined,
            unit: l.sku_snapshot.unit,
            ordered: l.quantity,
            done: l.posted,
            remaining: l.remaining,
            // A sale takes stock out, so the dialog shows what each warehouse can give.
            skuId: postingOrder.kind === 'SALE' ? l.sku_id : undefined,
          }))}
          warehouses={warehouses}
          footnote={`提交后${postingOrder.kind === 'PURCHASE' ? '增加' : '扣减'}本企业库存并写入流水。`}
          submit={(warehouseId, lines, key) =>
            api(`/local-orders/${postingOrder.id}/post`, { warehouseId, lines, key })
          }
          onClose={() => setPostingOrder(null)}
          onDone={() => {
            setPostingOrder(null);
            reload();
            notify('已登记出入库');
          }}
        />
      )}
      {orderEntry && bootstrap && (
        <TradeOrderDialog
          key={`${workspace}-${orderEntry}`}
          side={orderEntry}
          workspace={workspace}
          canCreateSku={roles.some((r) => r === 'admin' || r === 'warehouse')}
          bootstrap={bootstrap}
          onClose={(catalogChanged) => {
            setOrderEntry(null);
            if (catalogChanged) reload();
          }}
          onCreated={(id) => {
            setOrderEntry(null);
            reload();
            notify('操作已完成');
            navigate(id ? `/orders/${id}` : orderEntry === 'BUY' ? '/purchases' : '/sales');
          }}
        />
      )}
      {/* A single question gets a compact box with the field across it. */}
      <Dialog
        open={!!dialog}
        onClose={() => !busy && setDialog(null)}
        {...((dialog?.fields.length ?? 0) <= 1 && { fullWidth: true, maxWidth: 'xs' as const })}
        // Typing starts at once: the dialog takes focus as it opens, so the first text field
        // is focused once it has.
        slotProps={{
          transition: {
            onEntered: (node: HTMLElement) =>
              node
                .querySelector<HTMLInputElement>(
                  'input:not([type=hidden]):not([readonly]):not([aria-hidden=true]):not([tabindex="-1"])',
                )
                ?.focus(),
          },
        }}
      >
        <DialogTitle>{dialog?.title}</DialogTitle>
        <DialogContent>
          {dialog?.description && (
            <Typography
              color="text.secondary"
              sx={{
                fontSize: 13,
                mb: 2,
              }}
            >
              {dialog.description}
            </Typography>
          )}
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {dialogError}
            </Alert>
          )}
          <div
            className="field-grid"
            style={dialog?.fields.length ? undefined : { display: 'none' }}
          >
            {dialog?.fields.map((field) => {
              const single = dialog.fields.length === 1;
              if (field.multi) {
                const picked: string[] = dialog.form[field.name] ?? [];
                const toggle = (value: string) =>
                  setDialog((previous) =>
                    previous
                      ? {
                          ...previous,
                          form: {
                            ...previous.form,
                            [field.name]: picked.includes(value)
                              ? picked.filter((v) => v !== value)
                              : [...picked, value],
                          },
                        }
                      : null,
                  );
                return (
                  <FormControl key={field.name} className="wide" component="fieldset">
                    <FormLabel component="legend" sx={{ fontSize: 13, mb: 0.5 }}>
                      {field.label}
                    </FormLabel>
                    <FormGroup>
                      {field.options?.map((o) => (
                        <FormControlLabel
                          key={o.value}
                          disabled={busy}
                          sx={{ alignItems: 'flex-start', mr: 0, py: 0.25 }}
                          control={
                            <Checkbox
                              size="small"
                              sx={{ pt: 0.25 }}
                              checked={picked.includes(o.value)}
                              onChange={() => toggle(o.value)}
                            />
                          }
                          label={
                            <Box sx={{ pt: 0.25 }}>
                              <Typography sx={{ fontSize: 14 }}>{o.label}</Typography>
                              {o.hint && (
                                <Typography color="text.secondary" sx={{ fontSize: 12 }}>
                                  {o.hint}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      ))}
                    </FormGroup>
                  </FormControl>
                );
              }
              const setValue = (value: string) =>
                setDialog((previous) =>
                  previous
                    ? {
                        ...previous,
                        form: {
                          ...previous.form,
                          [field.name]:
                            field.decimals === undefined
                              ? value
                              : decimalInput(value, field.decimals, field.signed),
                        },
                      }
                    : null,
                );
              if (field.skus)
                return (
                  <Autocomplete<any>
                    key={field.name}
                    className={field.wide ? 'wide' : ''}
                    {...skuPicker()}
                    options={field.skus}
                    disabled={busy}
                    value={field.skus.find((s) => s.id === dialog.form[field.name]) ?? null}
                    onChange={(_, item) => setValue(item?.id ?? '')}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={field.label}
                        placeholder="搜索名称、规格或编码"
                      />
                    )}
                  />
                );
              // Pick a common value or type another one.
              if (field.suggestions)
                return (
                  <Autocomplete
                    key={field.name}
                    className={field.wide ? 'wide' : ''}
                    freeSolo
                    openOnFocus
                    disabled={busy}
                    options={field.suggestions}
                    inputValue={dialog.form[field.name] ?? ''}
                    onInputChange={(_, value) => setValue(value)}
                    renderInput={(params) => (
                      <TextField {...params} label={field.label} helperText={field.helper} />
                    )}
                  />
                );
              return (
                <TextField
                  key={field.name}
                  className={field.wide || single ? 'wide' : ''}
                  label={field.label}
                  type={field.type ?? 'text'}
                  select={!!field.options}
                  helperText={field.helper}
                  // With one field, Enter submits.
                  onKeyDown={(e) => {
                    if (
                      single &&
                      !field.options &&
                      e.key === 'Enter' &&
                      !e.nativeEvent.isComposing
                    ) {
                      e.preventDefault();
                      void submitDialog();
                    }
                  }}
                  disabled={busy}
                  value={dialog.form[field.name] ?? ''}
                  slotProps={{
                    input: {
                      readOnly: field.readOnly,
                      endAdornment: field.unit?.(dialog.form) && (
                        <InputAdornment position="end">{field.unit(dialog.form)}</InputAdornment>
                      ),
                    },
                    // Numbers: numeric keyboard, and no browser history dropdown.
                    ...(field.decimals !== undefined
                      ? { htmlInput: { inputMode: 'decimal', autoComplete: 'off' } }
                      : {}),
                    ...(field.type === 'date' ? { inputLabel: { shrink: true } } : {}),
                  }}
                  onChange={(e) => setValue(e.target.value)}
                >
                  {field.options?.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </TextField>
              );
            })}
          </div>
          {(() => {
            const notice = dialog?.notice?.(dialog.form);
            if (!notice) return null;
            const action = notice.action;
            return (
              <Alert
                severity="info"
                sx={{ mt: 2 }}
                action={
                  action && (
                    <Button
                      color="inherit"
                      size="small"
                      disabled={busy}
                      onClick={() => {
                        setDialog(null);
                        action.run();
                      }}
                    >
                      {action.label}
                    </Button>
                  )
                }
              >
                {notice.text}
              </Alert>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button disabled={busy} onClick={() => setDialog(null)}>
            取消
          </Button>
          <Button variant="contained" disabled={busy} onClick={submitDialog}>
            {busy ? '正在提交…' : (dialog?.confirmLabel ?? '确认提交')}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={!!toast}
        autoHideDuration={toast.length > 16 ? 6000 : 3000}
        onClose={() => setToast('')}
        message={toast}
      />
    </>
  );
}

/**
 * The enterprise's service, in one line, only when it needs attention: everyone sees read-only
 * (it explains why saving fails); the run-up to expiry is for the administrator, who renews.
 */
function ServiceNotice({ workspace }: { workspace?: SessionUser['workspaces'][number] }) {
  const service = workspace?.service;
  if (!service?.until) return null;
  const day = (v: string) => new Date(v).toLocaleDateString('zh-CN');
  const left = Math.ceil((new Date(service.until).getTime() - Date.now()) / 86400000);
  let message = '';
  if (service.state === 'READONLY')
    message = `服务已于 ${day(service.until)} 到期，当前为只读：可查看和导出数据，不能新建或修改。续费请联系平台。`;
  else if (!workspace?.roles.includes('admin')) return null;
  else if (service.state === 'GRACE')
    message = `服务已于 ${day(service.until)} 到期，${day(service.readOnlyAt!)} 起转为只读。续费请联系平台。`;
  else if (service.state === 'IN_SERVICE' && left <= (service.trial ? 7 : 30))
    message = `${service.trial ? '试用' : '服务'}将于 ${day(service.until)} 到期（剩 ${left} 天），续费请联系平台。`;
  if (!message) return null;
  return (
    <Alert severity={service.state === 'IN_SERVICE' ? 'info' : 'warning'} sx={{ mb: 2 }}>
      {message}
    </Alert>
  );
}
